/**
 * `#uc2Contrasts`: "Pairs with different results", the alike watersheds that sit in
 * **different** classes (one with positive change, one without), ranked by how alike they
 * are. One table row per pair on the tab's shared column rhythm (client, 7 October 2026):
 * the two names joined by a small "vs", how alike they are, and the chosen practice's
 * adoption on both sides as a paired mini-bar, the positive side in the positive green.
 *
 * A row opens in place (click, Enter or Space) to the pair's sentence and its two plain
 * notes, one pair at a time, so the table stays a table and the prose is there on demand.
 *
 * Precomputed on `uc2/summary`, so changing the practice group costs no request. The two
 * adoption figures are not in the pair (the engine stores the gap and the learning flag);
 * they come from the units rows the pane already holds (`adoptionOf`), the same payload the
 * practices chip paints the map from.
 */
import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { ChartFrame } from "../../components/charts/ChartFrame";
import type { IntelUc2ContrastPair } from "../../lib/api";
import { Icon } from "../../lib/icons";
import { BarReading } from "./Uc2TableParts";
import { Uc2Notes } from "./Uc2Notes";
import { pairLookOut, pairNoteBranch } from "./uc2Captions";
import {
  UC2_CATEGORY_COLORS,
  UC2_PROGRAMME_SCOPE,
  UC2_REST_COLOR,
  unitInScope,
} from "./uc2Constants";
import { alikePct, pairSentence, pctText } from "./uc2Table";

export interface Uc2ContrastsProps {
  pairs?: IntelUc2ContrastPair[] | undefined;
  /** `'programme'`, or the admin0 code the country level is reading. */
  scope: string;
  /** `#uc2Practice`: which group's bars, gap and learning flag the rows show. */
  practice: string;
  /** That group's label, and the same word lower-cased for mid-sentence use. */
  practiceLabel: string;
  practiceLower: string;
  /** One watershed's adoption of the selected group, from the units rows. */
  adoptionOf: (unitId: string) => number | null;
  /** Kept for the call site's shape; the table reads no programme mean. */
  programmeMean?: number | null;
  /** How many rows at most; the design stores up to ten. */
  limit?: number;
}

/** A pair belongs to a country scope when it says so, or when either id starts with it. */
function pairInScope(pair: IntelUc2ContrastPair, scope: string): boolean {
  if (scope === UC2_PROGRAMME_SCOPE) return true;
  if (pair.countries?.length) return pair.countries.includes(scope);
  return (
    unitInScope(pair.a.unit_id, scope) || unitInScope(pair.b.unit_id, scope)
  );
}

/** The side's bar colour: the positive green for positive change, the neutral for the rest. */
function colorOf(category: string | null | undefined): string {
  return category === "positive"
    ? UC2_CATEGORY_COLORS.positive!
    : UC2_REST_COLOR;
}

export function Uc2Contrasts({
  pairs,
  scope,
  practice,
  practiceLabel,
  practiceLower,
  adoptionOf,
  limit = 10,
}: Uc2ContrastsProps) {
  const { t } = useTranslation();
  const rows = (pairs ?? [])
    .filter((p) => pairInScope(p, scope))
    .slice(0, limit);
  const [open, setOpen] = useState<string | null>(null);
  const na = t("intel.uc2.table.na");
  const toggle = (id: string) => setOpen((cur) => (cur === id ? null : id));

  const table = (expanded: boolean) => (
    <div className="uc2-tablewrap">
      <table className="uc2-table is-stack uc2-table-pairs">
        <thead>
          <tr>
            <th scope="col" className="c-name">
              {t("intel.uc2.table.pair")}
            </th>
            <th scope="col" className="c-bar">
              {t("intel.uc2.table.alike")}
            </th>
            <th scope="col" className="c-val">
              {t("intel.uc2.table.adoption", { practice: practiceLabel })}
            </th>
            <th scope="col" className="c-chev">
              <span className="sr-only">{t("intel.uc2.table.details")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.flatMap((pair) => {
            const id = `${pair.a.unit_id}-${pair.b.unit_id}`;
            const gap =
              typeof pair.gaps?.[practice] === "number"
                ? pair.gaps[practice]!
                : null;
            const pct = alikePct(pair.similarity);
            const learning = Boolean(pair.learning?.[practice]);
            const isOpen = open === id;
            const detailId = `uc2-contrast-detail-${id}${expanded ? "-lb" : ""}`;
            const sides = [pair.a, pair.b].map((side) => ({
              ...side,
              value: adoptionOf(side.unit_id),
            }));
            const say = pairSentence(
              pair.a.name,
              pair.b.name,
              practiceLower,
              gap,
            );
            const missingSide = sides[0]!.value == null ? "a" : "b";
            const lookOut = pairLookOut(
              pairNoteBranch(gap, learning),
              { a: pair.a.name, b: pair.b.name },
              practiceLower,
              gap,
              missingSide,
            );
            const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle(id);
              }
            };
            const row = (
              <tr
                key={id}
                className={`uc2-pair-row${isOpen ? " is-open" : ""}`}
                data-testid={expanded ? undefined : `uc2-contrast-${id}`}
                tabIndex={0}
                aria-expanded={isOpen}
                aria-controls={isOpen ? detailId : undefined}
                onClick={() => toggle(id)}
                onKeyDown={onKey}
              >
                <td className="c-name">
                  <span className="uc2-name">{pair.a.name}</span>
                  <span className="uc2-vs"> {t("intel.uc2.table.vs")} </span>
                  <span className="uc2-name">{pair.b.name}</span>
                  {learning ? (
                    <span
                      className="uc2-tag"
                      data-testid={`uc2-contrast-tag-${id}`}
                    >
                      {t("intel.uc2.learningOpportunity")}
                    </span>
                  ) : null}
                </td>
                <td className="c-bar">
                  <BarReading
                    value={pct / 100}
                    text={pctText(pct)}
                    label={t("intel.uc2.table.alikeAria", { pct })}
                  />
                </td>
                <td className="c-val">
                  <span
                    className="uc2-pairbars"
                    data-testid={
                      expanded ? undefined : `uc2-contrast-bars-${id}`
                    }
                  >
                    {sides.map((side) => (
                      <BarReading
                        key={side.unit_id}
                        value={side.value == null ? null : side.value / 100}
                        text={side.value == null ? na : Math.round(side.value)}
                        color={colorOf(side.category)}
                        label={t("intel.uc2.table.adoptionAria", {
                          name: side.name,
                          practice: practiceLower,
                          value:
                            side.value == null ? na : Math.round(side.value),
                        })}
                      />
                    ))}
                  </span>
                </td>
                <td className="c-chev" aria-hidden="true">
                  <Icon name="chev" size={14} />
                </td>
              </tr>
            );
            if (!isOpen) return [row];
            return [
              row,
              <tr
                key={`${id}-detail`}
                className="uc2-pair-detail"
                id={detailId}
              >
                <td colSpan={4}>
                  <p
                    className="uc2-pair-say"
                    data-testid={expanded ? undefined : `uc2-pair-say-${id}`}
                  >
                    {t(say.key, say.options)}
                  </p>
                  <Uc2Notes
                    testId={
                      expanded
                        ? `uc2-contrast-note-${id}-lb`
                        : `uc2-contrast-note-${id}`
                    }
                    rows={[
                      {
                        label: t("intel.uc2.note.happening"),
                        lines: [
                          t("intel.uc2.note.pairHappening", {
                            a: pair.a.name,
                            b: pair.b.name,
                            pct,
                          }),
                        ],
                      },
                      {
                        label: t("intel.uc2.note.lookOut"),
                        lines: [t(lookOut.key, lookOut.options ?? {})],
                      },
                    ]}
                  />
                </td>
              </tr>,
            ];
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div id="uc2Contrasts" className="result-box">
      <h5>{t("intel.uc2.contrastsTitle")}</h5>
      <div className="result-pad">
        {rows.length ? (
          <ChartFrame
            title={t("intel.uc2.contrastsTitle")}
            expandLabel={t("intel.uc2.table.expand")}
            testId="uc2-contrasts-table"
          >
            {(size) => table(size.expanded)}
          </ChartFrame>
        ) : (
          <p className="uc2-quiet" data-testid="uc2-contrasts-empty">
            {t("intel.uc2.blockPending")}
          </p>
        )}
        <p className="uc2-table-say" data-testid="uc2-pairs-help">
          {t("intel.uc2.contrastsHelp")}
        </p>
      </div>
    </div>
  );
}

export default Uc2Contrasts;
